import { LoadingOverlay, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import useUpdateInspectionServiceMetadata from '../../../hooks/inspection-services/useUpdateInspectionServiceMetadata.ts';
import {
  UpdateInspectionServiceMetadataRequest,
  UpdateInspectionServiceOperationHoursRequest,
  UpdateInspectionServicePaymentMethodsRequest,
  UpdateInspectionServiceTagsRequest,
} from '../../../api/requests/inspection-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import useInspectionService from '../../../hooks/inspection-services/useInspectionService.ts';
import useUpdateInspectionServiceOperationHours from '../../../hooks/inspection-services/useUpdateInspectionServiceOperationHours.ts';
import { notifySuccess } from '../../../utils/NotifySuccess.tsx';
import useEnableInspectionService from '../../../hooks/inspection-services/useEnableInspectionService.ts';
import useDisableInspectionService from '../../../hooks/inspection-services/useDisableInspectionService.ts';
import usePauseInspectionService from '../../../hooks/inspection-services/usePauseInspectionService.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateUpdateInspectionServiceForm, {
  MutateUpdateInspectionServiceFormValues,
} from '../../ui/places/MutateUpdateInspectionServiceForm.tsx';
import { PhoneNumberUtil } from 'google-libphonenumber';
import useUpdateInspectionServiceTags from '../../../hooks/inspection-services/useUpdateInspectionServiceTags.ts';
import useUpdateInspectionServicePaymentMethods from '../../../hooks/inspection-services/useUpdateInspectionServicePaymentMethods.ts';
import useUpdateInspectionServiceStoreIds from '../../../hooks/inspection-services/useUpdateInspectionServiceStoreIds.ts';
import {
  buildStoreUrl,
  resolveStoreIdsChange,
} from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface UpdateInspectionServiceMetadataModalProps {
  placeId: string;
  inspectionServiceId: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UpdateInspectionServiceMetadataDrawer = ({
  placeId,
  inspectionServiceId,
  onSuccess,
  onCancel,
}: UpdateInspectionServiceMetadataModalProps) => {
  const queryClient = useQueryClient();

  const { data: inspectionService, isLoading: isInspectionServiceLoading } =
    useInspectionService(inspectionServiceId);

  const { mutateAsync: updateInspectionServiceMetadata } =
    useUpdateInspectionServiceMetadata();
  const { mutateAsync: updateInspectionServiceOperationHours } =
    useUpdateInspectionServiceOperationHours();
  const { mutateAsync: updateInspectionServiceTags } =
    useUpdateInspectionServiceTags();
  const { mutateAsync: updateInspectionServicePaymentMethods } =
    useUpdateInspectionServicePaymentMethods();
  const { mutateAsync: updateInspectionServiceStoreIds } =
    useUpdateInspectionServiceStoreIds();
  const { mutateAsync: enableInspectionService } = useEnableInspectionService();
  const { mutateAsync: pauseInspectionService } = usePauseInspectionService();
  const { mutateAsync: disableInspectionService } =
    useDisableInspectionService();

  const {
    mutate: handleSubmitAll,
    isPending: isUpdateInspectionServicePending,
  } = useMutation<void, ErrorResponse, MutateUpdateInspectionServiceFormValues>(
    {
      mutationFn: async (formValues) => {
        const storeIdsToSubmit = resolveStoreIdsChange(
          formValues.storeIdUrl,
          'INSPECTION',
          inspectionService.storeIds,
        );
        if (storeIdsToSubmit !== null) {
          await updateInspectionServiceStoreIds({
            serviceId: inspectionService.id,
            storeIds: storeIdsToSubmit,
          });
        }

        const parsedPhoneNumber =
          formValues.phoneNumberCountryCode !== '' &&
          formValues.phoneNumberValue !== ''
            ? phoneUtil.parse(
                `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
              )
            : null;

        const updateInspectionServiceMetadataRequest: UpdateInspectionServiceMetadataRequest =
          {
            serviceId: inspectionService.id,
            name: formValues.name.trim(),
            brandId: formValues.brandId ?? '',
            phoneNumber: {
              countryCode: formValues.phoneNumberCountryCode ?? '',
              number: parsedPhoneNumber
                ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
                : '',
            },
            website: formValues.website,
            notes: formValues.notes,
            pricingSource: inspectionService.pricingSource,
          };

        await updateInspectionServiceMetadata(
          updateInspectionServiceMetadataRequest,
        );

        if (formValues.serviceStatus === 'ONLINE') {
          await enableInspectionService(inspectionService.id);
        } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
          await pauseInspectionService(inspectionService.id);
        } else {
          await disableInspectionService(inspectionService.id);
        }

        const updateInspectionServiceTagsRequest: UpdateInspectionServiceTagsRequest =
          {
            serviceId: inspectionService.id,
            tags: formValues.tags,
          };

        await updateInspectionServiceTags(updateInspectionServiceTagsRequest);

        const updateInspectionServicePaymentMethodsRequest: UpdateInspectionServicePaymentMethodsRequest =
          {
            serviceId: inspectionService.id,
            paymentMethods: formValues.paymentMethods,
          };

        await updateInspectionServicePaymentMethods(
          updateInspectionServicePaymentMethodsRequest,
        );

        // 一律送出表單當下顯示的營業時間，確保 drawer 上看到的資料跟實際寫入的完全一致
        const updateInspectionServiceOperationHoursRequest: UpdateInspectionServiceOperationHoursRequest =
          {
            serviceId: inspectionServiceId,
            operationHours: formValues.operationHours,
          };

        await updateInspectionServiceOperationHours(
          updateInspectionServiceOperationHoursRequest,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['inspectionServices', inspectionService.id],
          exact: true,
        });
        await queryClient.invalidateQueries({
          queryKey: ['places', placeId],
          exact: true,
        });
        notifySuccess('更新驗車服務成功');
        onSuccess();
      },
      onError: (error) => {
        notifyError('更新驗車服務失敗', error.message);
      },
    },
  );

  const handleFormSubmit = (
    formValues: MutateUpdateInspectionServiceFormValues,
  ) => {
    let storeIdsToSubmit: ReturnType<typeof resolveStoreIdsChange>;
    try {
      storeIdsToSubmit = resolveStoreIdsChange(
        formValues.storeIdUrl,
        'INSPECTION',
        inspectionService.storeIds,
      );
    } catch {
      return;
    }

    if (storeIdsToSubmit === null) {
      handleSubmitAll(formValues);
      return;
    }

    const isUnbinding = storeIdsToSubmit.length === 0;
    modals.openConfirmModal({
      title: (
        <Text style={{ fontSize: 16, fontWeight: 700 }}>
          {isUnbinding ? '解除綁定商店' : '綁定商店'}
        </Text>
      ),
      children: (
        <Text size="sm">
          {isUnbinding
            ? '解除綁定後，目前的營業時間不會被清空，會維持最後一次同步的資料，確定要繼續嗎？'
            : '綁定後將會用 autoplus 現有的營業時間覆蓋這個服務目前的資料，確定要繼續嗎？'}
        </Text>
      ),
      labels: { confirm: '是，我確定', cancel: '取消' },
      confirmProps: isUnbinding ? { color: 'red' } : undefined,
      onConfirm: () => handleSubmitAll(formValues),
    });
  };

  const handleClose = () => {
    onCancel();
  };

  if (isInspectionServiceLoading) return <LoadingOverlay />;

  return (
    <MutateUpdateInspectionServiceForm
      initialValues={{
        serviceId: inspectionService.id,
        name: inspectionService.name,
        serviceStatus: inspectionService.serviceStatus,
        brandId: inspectionService.brandId,
        storeIdUrl: inspectionService.storeIds[0]
          ? buildStoreUrl(inspectionService.storeIds[0])
          : '',
        operationHours: inspectionService.operationHours,
        phoneNumberCountryCode: inspectionService.phoneNumber?.countryCode,
        phoneNumberValue: inspectionService.phoneNumber?.number,
        website: inspectionService.website,
        notes: inspectionService.notes,
        tags: inspectionService.tags,
        paymentMethods: inspectionService.paymentMethods,
      }}
      onSubmit={handleFormSubmit}
      onCancel={handleClose}
      isSubmitting={isUpdateInspectionServicePending}
      isUpdating={true}
    />
  );
};

export { UpdateInspectionServiceMetadataDrawer };
